import { type ReactElement } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, Collapse, Skeleton, TableCell, TableRow } from '@/ui/primitives';
import {
  CHILD_NAME_INDENT,
  EXPAND_ICON_GUTTER_WIDTH,
  HistoryNameCell,
  HistoryStatusesGrid,
  type NonArraySx,
} from './EntityDailyHistoryPanelRowParts';
import type { EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';

type EntityDailyHistoryRowsProps = {
  rows: EntityDailyHistoryRow[];
  expandedRowIds: Set<string>;
  onToggleRowExpansion: (rowId: string) => void;
  rowHeight: number;
  blockSize: number;
  nameColumnWidth: string;
  rowNameTextSx: NonArraySx;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  linksDisabled: boolean;
};

const INDENT_STEP_PX = 24;
const BASE_CHILD_INDENT_PX = Number.parseInt(CHILD_NAME_INDENT, 10);

const renderNestedHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
  nestingLevel,
}: EntityDailyHistoryRowsProps & {
  nestingLevel: number;
}): ReactElement[] =>
  rows.flatMap((row) => {
    const isExpanded = expandedRowIds.has(row.id);
    const hasChildren =
      row.hasExpandableChildren ?? Boolean(row.childRows && row.childRows.length > 0);
    const childIndent =
      nestingLevel === 0
        ? CHILD_NAME_INDENT
        : `${String(BASE_CHILD_INDENT_PX + (nestingLevel - 1) * INDENT_STEP_PX)}px`;

    const rowElements: ReactElement[] = [
      <Box
        key={`${row.id}-nested-row`}
        sx={{
          backgroundColor: 'background.paper',
          display: 'grid',
          gridTemplateColumns: `${nameColumnWidth} minmax(0, 1fr)`,
          alignItems: 'center',
          minHeight: `${rowHeight}px`,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            px: s('xl'),
            py: s('none'),
            overflow: 'hidden',
          }}
        >
          <HistoryNameCell
            row={row}
            rowNameTextSx={rowNameTextSx}
            childRowIndent={childIndent}
            nameColor="textSecondary"
            showExpand={hasChildren}
            isExpanded={isExpanded}
            onToggle={
              hasChildren
                ? () => {
                    onToggleRowExpansion(row.id);
                  }
                : undefined
            }
            linksDisabled={linksDisabled}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            px: s('xl'),
            py: s('none'),
            minWidth: 0,
            width: '100%',
          }}
        >
          <HistoryStatusesGrid
            rowId={row.id}
            dailyHistory={row.dailyHistory}
            blockSize={blockSize}
            activityLabel={activityLabel}
            linksDisabled={linksDisabled}
          />
        </Box>
      </Box>,
    ];

    if (!hasChildren) {
      return rowElements;
    }

    rowElements.push(
      <Collapse
        key={`${row.id}-nested-children`}
        in={isExpanded}
        timeout="auto"
        sx={{
          '& .MuiCollapse-wrapperInner': {
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ backgroundColor: 'background.paper' }}>
          {row.childrenLoading
            ? Array.from({ length: row.childrenLoadingRowCount ?? 2 }, (_unused, index) => (
                <Box
                  key={`${row.id}-loading-${String(index)}`}
                  sx={{
                    backgroundColor: 'background.paper',
                    display: 'grid',
                    gridTemplateColumns: `${nameColumnWidth} minmax(0, 1fr)`,
                    alignItems: 'center',
                    minHeight: `${rowHeight}px`,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      px: s('xl'),
                      py: s('none'),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        width: '100%',
                        pl: childIndent,
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={index % 2 === 0 ? '68%' : '61%'}
                        sx={{
                          height: '14px',
                          transform: 'none',
                          borderRadius: r('pill'),
                          flexShrink: 0,
                        }}
                      />
                      <Box
                        sx={{
                          width: EXPAND_ICON_GUTTER_WIDTH,
                          ml: s('sm'),
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      px: s('xl'),
                      py: s('none'),
                      minWidth: 0,
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: `repeat(${String(row.dailyHistory.length)}, minmax(0, 1fr))`,
                      columnGap: s('md'),
                      alignItems: 'center',
                    }}
                  >
                    {row.dailyHistory.map((historyCell) => (
                      <Skeleton
                        key={`${row.id}-${historyCell.day.toISOString()}-loading-${String(index)}`}
                        variant="rounded"
                        sx={{
                          width: '100%',
                          minWidth: `${blockSize}px`,
                          height: '14px',
                          transform: 'none',
                          borderRadius: r('sm'),
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            : null}
          {renderNestedHistoryRows({
            rows: row.childRows ?? [],
            expandedRowIds,
            onToggleRowExpansion,
            rowHeight,
            blockSize,
            nameColumnWidth,
            rowNameTextSx,
            activityLabel,
            linksDisabled,
            nestingLevel: nestingLevel + 1,
          })}
        </Box>
      </Collapse>,
    );

    return rowElements;
  });

const renderHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
  nestingLevel,
}: EntityDailyHistoryRowsProps & {
  nestingLevel: number;
}): ReactElement[] =>
  rows.flatMap((row) => {
    const isExpanded = expandedRowIds.has(row.id);
    const hasChildren =
      row.hasExpandableChildren ?? Boolean(row.childRows && row.childRows.length > 0);
    const childGridFirstColumnWidth = nameColumnWidth;
    const childIndent =
      nestingLevel === 0
        ? CHILD_NAME_INDENT
        : `${String(BASE_CHILD_INDENT_PX + (nestingLevel - 1) * INDENT_STEP_PX)}px`;
    const rowElements: ReactElement[] = [
      <TableRow
        key={`${row.id}-row`}
        sx={{
          backgroundColor: 'background.paper',
          height: `${rowHeight}px`,
        }}
      >
        <TableCell
          sx={{
            backgroundColor: 'background.paper',
            width: nameColumnWidth,
            minWidth: nameColumnWidth,
            maxWidth: nameColumnWidth,
            px: s('xl'),
            py: s('none'),
            verticalAlign: 'middle',
            borderBottom: 'none',
          }}
        >
          <HistoryNameCell
            row={row}
            rowNameTextSx={rowNameTextSx}
            childRowIndent={nestingLevel > 0 ? childIndent : undefined}
            nameColor={nestingLevel > 0 ? 'textSecondary' : 'inherit'}
            showExpand={hasChildren}
            isExpanded={isExpanded}
            onToggle={
              hasChildren
                ? () => {
                    onToggleRowExpansion(row.id);
                  }
                : undefined
            }
            linksDisabled={linksDisabled}
          />
        </TableCell>
        <TableCell
          sx={{
            backgroundColor: 'background.paper',
            px: s('xl'),
            py: s('none'),
            verticalAlign: 'middle',
            borderBottom: 'none',
            width: '100%',
          }}
        >
          <HistoryStatusesGrid
            rowId={row.id}
            dailyHistory={row.dailyHistory}
            blockSize={blockSize}
            activityLabel={activityLabel}
            linksDisabled={linksDisabled}
          />
        </TableCell>
      </TableRow>,
    ];

    if (!hasChildren) {
      return rowElements;
    }

    rowElements.push(
      <TableRow
        key={`${row.id}-children`}
        sx={{ backgroundColor: 'background.paper', '& td': { borderBottom: 'none' } }}
      >
        <TableCell
          colSpan={2}
          sx={{ backgroundColor: 'background.paper', p: s('none'), borderBottom: 'none' }}
        >
          <Collapse
            in={isExpanded}
            timeout="auto"
            sx={{
              '& .MuiCollapse-wrapperInner': {
                overflow: 'hidden',
              },
            }}
          >
            <Box>
              {row.childrenLoading
                ? Array.from({ length: row.childrenLoadingRowCount ?? 2 }, (_unused, index) => (
                    <Box
                      key={`${row.id}-loading-${String(index)}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `${childGridFirstColumnWidth} minmax(0, 1fr)`,
                        alignItems: 'center',
                        minHeight: `${rowHeight}px`,
                      }}
                    >
                      <Box
                        sx={{
                          px: s('xl'),
                          py: s('none'),
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            width: '100%',
                            pl: childIndent,
                          }}
                        >
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '68%' : '61%'}
                            sx={{
                              height: '14px',
                              transform: 'none',
                              borderRadius: r('pill'),
                              flexShrink: 0,
                            }}
                          />
                          <Box
                            sx={{
                              width: EXPAND_ICON_GUTTER_WIDTH,
                              ml: s('sm'),
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          px: s('xl'),
                          py: s('none'),
                          minWidth: 0,
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: `repeat(${String(row.dailyHistory.length)}, minmax(0, 1fr))`,
                          columnGap: s('md'),
                          alignItems: 'center',
                        }}
                      >
                        {row.dailyHistory.map((historyCell) => (
                          <Skeleton
                            key={`${row.id}-${historyCell.day.toISOString()}-loading-${String(index)}`}
                            variant="rounded"
                            sx={{
                              width: '100%',
                              minWidth: `${blockSize}px`,
                              height: '14px',
                              transform: 'none',
                              borderRadius: r('sm'),
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))
                : null}
              {renderNestedHistoryRows({
                rows: row.childRows ?? [],
                expandedRowIds,
                onToggleRowExpansion,
                rowHeight,
                blockSize,
                nameColumnWidth,
                rowNameTextSx,
                activityLabel,
                linksDisabled,
                nestingLevel: nestingLevel + 1,
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>,
    );

    return rowElements;
  });

export const EntityDailyHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
}: EntityDailyHistoryRowsProps): ReactElement => (
  <>
    {renderHistoryRows({
      rows,
      expandedRowIds,
      onToggleRowExpansion,
      rowHeight,
      blockSize,
      nameColumnWidth,
      rowNameTextSx,
      activityLabel,
      linksDisabled,
      nestingLevel: 0,
    })}
  </>
);
